import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Button,
} from '@mantine/core'
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react'
import { useArchbaseRemoteServiceApi } from '@archbase/data'
import { API_TYPE } from '@ioc/IOCTypes'
import type { DoclenzJobService } from '@services/DoclenzJobService'
import type { JobSummaryDto } from '@domain/job/JobDto'

export function JobsView() {
  const jobApi = useArchbaseRemoteServiceApi<DoclenzJobService>(API_TYPE.DoclenzJob)
  const [jobs, setJobs] = useState<JobSummaryDto[]>([])
  const [tenantId, setTenantId] = useState('')
  const [status, setStatus] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await jobApi.findAll({
        tenantId: tenantId.trim() || undefined,
        status: status || undefined,
        limit: 100,
      })
      setJobs(response.jobs || [])
    } catch (err: any) {
      setError(err?.message || 'Não foi possível carregar os jobs.')
    } finally {
      setLoading(false)
    }
  }, [jobApi, tenantId, status])

  useEffect(() => {
    load()
  }, [load])

  return (
    /*
      O contêiner de abas do Archbase tem altura definida e não rola: sem isto a lista cresce
      além do fundo da tela e as últimas linhas simplesmente somem, sem barra nenhuma. A rolagem
      tem de ser da view, não da página.

      minHeight 0 não é decoração: num filho de flex o padrão é `auto`, que impede o encolhimento
      e faz o overflow vazar em vez de rolar.
    */
    <Stack
      p="md"
      gap="lg"
      style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}
    >
      <Group justify="space-between">
        <Title order={3}>Jobs</Title>
        <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={load}>
          Atualizar
        </Button>
      </Group>

      <Alert color="blue" variant="light">
        Só aparecem os jobs dentro da janela de retenção — 1 h enquanto processam, 24 h depois de
        concluídos. É visão operacional, não histórico.
      </Alert>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Group>
        <TextInput
          placeholder="Filtrar por tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.currentTarget.value)}
          w={220}
        />
        <Select
          placeholder="Situação"
          data={[
            { value: 'processing', label: 'processando' },
            { value: 'completed', label: 'concluído' },
            { value: 'failed', label: 'falhou' },
          ]}
          value={status}
          onChange={setStatus}
          clearable
          w={180}
        />
      </Group>

      <Card withBorder radius="md" p="md">
        {loading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={820}>
            {/* Job id, tenant e horarios ja passam da largura util em notebook. Sem ScrollContainer a tabela empurra o layout
              para fora da viewport em vez de rolar dentro do card. */}
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Arquivo</Table.Th>
                <Table.Th>Tenant</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th ta="right">Páginas</Table.Th>
                <Table.Th ta="right">UPs</Table.Th>
                <Table.Th ta="right">Tempo</Table.Th>
                <Table.Th>Situação</Table.Th>
                <Table.Th>Criado em</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {jobs.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      Nenhum job na janela de retenção.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {jobs.map((job) => (
                <Table.Tr key={job.job_id}>
                  <Table.Td>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {job.filename || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{job.tenant_id || '—'}</Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {job.mime_type || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">{job.pages ?? '—'}</Table.Td>
                  <Table.Td ta="right">{job.ups_consumed}</Table.Td>
                  <Table.Td ta="right">{job.processing_ms ? `${job.processing_ms} ms` : '—'}</Table.Td>
                  <Table.Td>
                    <JobStatusBadge status={job.status} errorCode={job.error_code} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {job.created_at ? new Date(job.created_at).toLocaleString('pt-BR') : '—'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </Stack>
  )
}

function JobStatusBadge({ status, errorCode }: { status: string; errorCode: string | null }) {
  if (status === 'completed') return <Badge color="green" variant="light">concluído</Badge>
  if (status === 'processing') return <Badge color="blue" variant="light">processando</Badge>
  return (
    <Badge color="red" variant="light">
      {errorCode || status}
    </Badge>
  )
}
